import { useCallback, useEffect, useMemo, useState } from "react";
import { ColOption, Table, TableRow, usePagination } from "@coolset/cs-ui";
import { cacheKeys } from "config";
import { useQueryClient } from "react-query";
import { useTagsData } from "../hooks/useTagsData";
import { tagsClient } from "../client/tagsClient";
import { TitleCell } from "shared/components/Table/TitleCell";
import { AddEditEntityForm, AddEditFormFields, AddEditFormFieldsType } from "shared/components/Form/AddEditEntityForm";
import { useSurveysData } from "modules/Surveys/hooks/useSurveysData";
import { surveyTagsClient } from "../client/surveyTagsClient";

const TitleCellClass = "_d-flex-ali-center-jc-sb-sm-gap title-cell";

export const TagsTable = (props: SupportingInfosTableProps) => {
  const { isAddingTag, onClose } = props;
  const [tagToEdit, setTagToEdit] = useState<string | number | null>(null);
  const [nameValue, setNameValue] = useState<string>("");
  const queryClient = useQueryClient();
  const paginationStore = usePagination("tagsPageSize");
  const { tags, tagsCount, isLoadingTags, tag, isLoadingTag } = useTagsData(paginationStore.apiParams, tagToEdit);
  const { surveys: surveysOnTagToEdit } = useSurveysData({
    limit: 1000,
    offset: 0,
    tags: tag ? [tag.name] : [],
  });
  const { surveys } = useSurveysData({
    limit: 1000,
    offset: 0,
  });

  useEffect(() => paginationStore.setRowCount(tagsCount), [paginationStore, tagsCount]);

  useEffect(() => {
    setTagToEdit(null);
    onClose();
  }, [paginationStore.apiParams, onClose]);

  const colOptions = useMemo<ColOption[]>(
    () => [
      { label: "Name", width: "minmax(400px, 2fr)" },
      { label: "No. of surveys", width: "minmax(200px, 1fr)" },
      { label: "Created", width: "minmax(200px, 1fr)" },
      { label: "Modified", width: "minmax(200px, 1fr)" },
    ],
    []
  );

  const handleTagSelect = useCallback(
    (id: string | number) => {
      setTagToEdit(id);
      onClose();
    },
    [setTagToEdit, onClose]
  );

  const handleOnCloseForm = useCallback(() => {
    setTagToEdit(null);
    onClose();
  }, [setTagToEdit, onClose]);

  const handleOnSave = useCallback(async () => {
    if (isAddingTag) {
      await tagsClient.createTag({ name: nameValue });
      queryClient.invalidateQueries(cacheKeys.tags.getTags);
    }
  }, [isAddingTag, nameValue, queryClient]);

  const handleOnDelete = useCallback(async () => {
    if (!tagToEdit) return;

    await tagsClient.deleteTag(tagToEdit);
    queryClient.invalidateQueries(cacheKeys.tags.getTags);
  }, [queryClient, tagToEdit]);

  const rows = useMemo<TableRow[]>(
    () => [
      ...(isAddingTag
        ? [
            {
              cells: [
                {
                  content: <TitleCell title="New tag" />,
                  className: TitleCellClass,
                },
                { content: "-" },
                { content: "-" },
                { content: "-" },
              ],
              isSelected: true,
            },
          ]
        : []),
      ...tags.map((tag) => ({
        cells: [
          {
            content: <TitleCell title={tag.name} />,
            className: TitleCellClass,
            onClick: () => handleTagSelect(tag.id),
          },
          { content: "placeholder", isOneLine: true },
          { content: "placeholder", isOneLine: true },
          { content: "placeholder", isOneLine: true },
        ],
        isSelected: tag.id === tagToEdit,
      })),
    ],
    [isAddingTag, tags, tagToEdit, handleTagSelect]
  );

  const handleChangeSurveys = useCallback(async (value: string, isRemove?: boolean) => {
    const survey = surveys.find((s) => s.id === Number(value));
    if (!survey) return;

    const updatedTags = survey.tags.map((t) => t.name);

    if (isRemove) {
      const index = updatedTags.indexOf(nameValue);
      updatedTags.splice(index, 1);
    } else {
      updatedTags.push(nameValue);
    }

    await surveyTagsClient.setSurveyTags(survey.id, updatedTags);
    queryClient.invalidateQueries(cacheKeys.surveys.getSurveys);
  }, [nameValue, queryClient, surveys]);

  const formFields = useMemo<AddEditFormFields[]>(
    () => [
      {
        title: "Name",
        value: nameValue,
        inputType: AddEditFormFieldsType.Text,
        onChange: setNameValue,
        isRequired: true,
      },
      ...(!isAddingTag ? [{
        title: "Surveys",
        value: surveysOnTagToEdit.map((s) => s.id).join(","),
        inputType: AddEditFormFieldsType.Connect,
        onChange: handleChangeSurveys,
        isRequired: false,
        options: surveys.map((s) => ({ label: s.title, value: String(s.id) })),
        selectLabel: "Add to survey",
      }] : [])
    ],
    [handleChangeSurveys, isAddingTag, nameValue, surveys, surveysOnTagToEdit]
  );

  useEffect(() => {
    if (isAddingTag) {
      setTagToEdit(null);
    }
  }, [isAddingTag]);

  useEffect(() => {
    if (isAddingTag) {
      setNameValue("");
    } else {
      setNameValue(tag?.name || "");
    }
  }, [tag, isAddingTag]);

  return (
    <div className="layout-entity-table">
      <div className="table-container">
        <Table colOptions={colOptions} rows={rows} isLoading={isLoadingTags} paginationStore={paginationStore} />
      </div>
      {(tagToEdit || isAddingTag) && (
        <AddEditEntityForm
          isNew={isAddingTag}
          entityId={tagToEdit}
          isLoading={isLoadingTag}
          fields={formFields}
          onSave={handleOnSave}
          onClose={handleOnCloseForm}
          onDelete={handleOnDelete}
          newTitle="New tag"
          entityTitle={tag?.name || ""}
          isJustDeleteWhenEditing
        />
      )}
    </div>
  );
};

interface SupportingInfosTableProps {
  isAddingTag: boolean;
  onClose: () => void;
}
